import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@backstage/core-plugin-api';
import {
  AccessRequestHookUpdateAction,
  PendingAccessRequestHooksFacets,
  PendingAccessRequestHooksByProductOptions,
  PendingAccessRequestHooksByProductFacetsOptions,
  PendingAccessRequestHooksResponse,
} from '@agilelab/plugin-wb-action-handler-common';
import { customAlertApiRef } from '@agilelab/plugin-wb-platform';
import { accessControlListApiRef } from '../AccessControlListApiRef';
import { bulkUpdateAccessRequestHooks } from '../utils';

const PENDING_ACCESS_REQUESTS_KEY = 'pending-access-request-hooks';
const STALE_TIME = 1000 * 60; // 60 seconds

export function usePendingAccessRequestHooks(
  productInstanceId: number | undefined,
  options?: PendingAccessRequestHooksByProductOptions,
) {
  const api = useApi(accessControlListApiRef);

  return useQuery<PendingAccessRequestHooksResponse>({
    queryKey: [
      PENDING_ACCESS_REQUESTS_KEY,
      productInstanceId,
      options?.filters?.consumableInterface,
      options?.filters?.requester,
      options?.filters?.requestedFor,
      options?.filters?.hookName,
      options?.pagination?.offset,
      options?.pagination?.limit,
      options?.sort?.sortBy,
      options?.sort?.sortOrder,
    ],
    queryFn: () =>
      api.getPendingAccessRequestHooksByProduct(productInstanceId!, options),
    enabled: productInstanceId !== undefined,
    staleTime: STALE_TIME,
  });
}
export function useUpdatePendingAccessRequestHook() {
  const api = useApi(accessControlListApiRef);
  const alertApi = useApi(customAlertApiRef);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      hookId,
      action,
    }: {
      hookId: string;
      action: AccessRequestHookUpdateAction;
    }) => api.updatePendingAccessRequestHook(hookId, action),
    onSuccess: (_data, variables) => {
      alertApi.post({
        message: `Access request ${
          variables.action === 'accept' ? 'approved' : 'rejected'
        } successfully`,
        severity: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: [PENDING_ACCESS_REQUESTS_KEY],
      });
      queryClient.invalidateQueries({ queryKey: ['paginatedByRecipient'] });
      queryClient.invalidateQueries({ queryKey: ['countUnreadByRecipient'] });
    },
    onError: (error: Error, variables) => {
      const isConflict = error.message?.includes('already been processed');
      alertApi.post({
        message: `Failed to ${
          variables.action === 'accept' ? 'approve' : 'reject'
        } access request: ${error.message}`,
        severity: 'error',
      });
      if (isConflict) {
        queryClient.invalidateQueries({
          queryKey: [PENDING_ACCESS_REQUESTS_KEY],
        });
      }
    },
  });
}

export function useBulkUpdatePendingAccessRequestHooks() {
  const api = useApi(accessControlListApiRef);
  const alertApi = useApi(customAlertApiRef);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      hookIds,
      action,
    }: {
      hookIds: string[];
      action: AccessRequestHookUpdateAction;
    }) => bulkUpdateAccessRequestHooks(hookIds, action, api),
    onSuccess: data => {
      const succeeded = data.filter(r => r.success).length;
      const failed = data.filter(r => !r.success).length;

      if (failed === 0) {
        alertApi.post({
          message: `${succeeded} request(s) processed successfully`,
          severity: 'success',
        });
      } else {
        alertApi.post({
          message: `${succeeded} succeeded, ${failed} failed`,
          severity: 'warning',
        });
      }

      queryClient.invalidateQueries({
        queryKey: [PENDING_ACCESS_REQUESTS_KEY],
      });
      // refresh notifications
      queryClient.invalidateQueries({ queryKey: ['paginatedByRecipient'] });
      queryClient.invalidateQueries({ queryKey: ['countUnreadByRecipient'] });
    },
    onError: (error: Error) => {
      alertApi.post({
        message: `Bulk operation failed: ${error.message}`,
        severity: 'error',
      });
    },
  });
}

/**
 * Fetches filter facets for the pending access requests table.
 * Uses cross-filter counting: each dimension is counted while all OTHER
 * active filters are applied, so facet counts remain non-zero for already-
 * selected options.
 * Pass `options.facets` to limit which facets are computed (all if omitted).
 */
export function usePendingAccessRequestHooksFacets(
  productInstanceId: number,
  options?: PendingAccessRequestHooksByProductFacetsOptions & {
    enabled?: boolean;
  },
) {
  const api = useApi(accessControlListApiRef);

  const result = useQuery<PendingAccessRequestHooksFacets>({
    queryKey: [
      'pending-access-request-hooks-facets',
      productInstanceId,
      options?.filter?.consumableInterface,
      options?.filter?.requester,
      options?.filter?.requestedFor,
      options?.filter?.hookName,
      options?.facets,
    ],
    queryFn: () =>
      api.getPendingAccessRequestHooksByProductFilterFacets(productInstanceId, {
        filter: options?.filter,
        facets: options?.facets,
      }),
    enabled: options?.enabled,
  });

  return result;
}
